"use client";

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./Popover";
import { Button } from "./Button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "./Command";
import { cn } from "@repo/shared/utils";

export interface ComboboxProps {
  value?: string;
  onChange:
    | ((label: string, value: string | number) => void)
    | ((label: string) => void);
  options: { value: string; label: string }[];
  placeholder?: string;
  emptyText?: string;
}

export const Combobox = ({
  value,
  onChange,
  options,
  placeholder,
  emptyText,
}: ComboboxProps) => {
  const [open, setOpen] = React.useState(false);

  const handleChange = (currentValue: string, optVal: string) => {
    onChange(currentValue === value ? "" : currentValue, optVal);
    setOpen(false);
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {value
            ? options.find((option) => option.value === value)?.label
            : placeholder ?? "Select..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-full p-0">
        <Command>
          <CommandInput placeholder={placeholder ?? "Search..."} />
          <CommandEmpty>{emptyText ?? "Nothing found."}</CommandEmpty>
          <CommandGroup className="overflow-auto max-h-96">
            {options.map((option) => (
              <CommandItem
                key={option.value}
                value={option.value}
                onSelect={(value) => handleChange(value, option.value)}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    value === option.value ? "opacity-100" : "opacity-0"
                  )}
                />
                {option.label}
              </CommandItem>
            ))}
          </CommandGroup>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
